import { FC } from 'react';
import { BoxV2 as Box, Flex } from 'portal-commons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquareCheck, faSquareX } from '@fortawesome/pro-regular-svg-icons';

import { CampaignDetail } from '@/apis/core/types';
import { convertTimeWithTimezone } from '@/utils/time';
import { remapTCRStatus } from '../../utils';

interface CampaignSummaryProps {
  campaign?: CampaignDetail;
  currentStatus: {
    rcsVet: boolean;
    logo: boolean;
    banner: boolean;
  };
}

const CurrentStatusField = ({
  label,
  validity = false,
}: {
  label: string;
  validity: boolean;
}) => {
  return (
    <Flex
      sx={{
        alignItems: 'center',
        gap: '6px',
        color: '#101820',
        fontSize: 'H300',
      }}
    >
      <FontAwesomeIcon
        icon={validity ? faSquareCheck : faSquareX}
        color={validity ? '#1B8103' : '#CF0A0A'}
      />
      <label>{label}</label>
    </Flex>
  );
};

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
  fullWidth?: boolean;
}) => {
  return (
    <Flex
      sx={{
        fontSize: 'H200',
        fontWeight: 'normal',
        color: '#6D6E70',
        lineHeight: '20px',
      }}
    >
      <label>{label}:</label>
      <Box sx={{ ml: '6px', color: '#101820', fontSize: 'H300', flex: '1' }}>
        {children ?? 'N/A'}
      </Box>
    </Flex>
  );
};

const Divider = () => (
  <Box
    as="span"
    sx={{
      color: '#6D6E70',
      mx: 'xs',
    }}
  >
    |
  </Box>
);

const CampaignSummary: FC<CampaignSummaryProps> = ({
  campaign,
  currentStatus,
}) => {
  return (
    <Box sx={{ mb: 'xxxxl' }}>
      <Flex sx={{ alignItems: 'baseline', mb: 's' }}>
        <Box
          as="h1"
          sx={{
            fontSize: 'H600',
            fontWeight: 400,
            mr: 's',
            mb: 's',
            color: 't.black80',
          }}
        >
          RCS Campaign ID:
        </Box>
        <Box
          as="span"
          sx={{ fontSize: '24px', fontWeight: 500, color: 't.blue800' }}
        >
          {campaign?.uid}
        </Box>
      </Flex>

      <Flex sx={{ mb: 'xs', color: 't.black60', alignItems: 'center' }}>
        <Field label="AGENT ID">{campaign?.googleAgentId}</Field>
        <Divider />
        <Field label="REGISTERED">
          {convertTimeWithTimezone(campaign?.createDate, 'MM/DD/YYYY')}
        </Field>
        <Divider />
        <Field label="USE-CASE">{campaign?.usecase}</Field>
        <Divider />
        <Field label="TCR STATUS">
          {remapTCRStatus(campaign?.status || '')}
        </Field>
      </Flex>

      <Field label="CURRENT STATUS">
        <Flex sx={{ gap: 'xs' }}>
          <CurrentStatusField label="RCS Vet" validity={currentStatus.rcsVet} />
          <Divider />
          <CurrentStatusField label="Logo" validity={currentStatus.logo} />
          <Divider />
          <CurrentStatusField label="Banner" validity={currentStatus.banner} />
        </Flex>
      </Field>
    </Box>
  );
};

export default CampaignSummary;
